import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { DESK, SHELL } from "../../constants/testIds";
import { useAppData } from "../../contexts/AppDataContext";
import { useCelebration } from "../../contexts/CelebrationContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FormField, isFieldValid } from "../common/FormField";
import { SubmitButton } from "../common/SubmitButton";
import {
  MemberPersonalFields, EMPTY_PERSONAL_FORM, isPersonalFormValid, buildPersonalPayload,
} from "../desk/MemberPersonalFields";
import { MembershipPaymentStep } from "../desk/MembershipPaymentStep";
import { HealthGoalsFields } from "../desk/HealthGoalsFields";

const PLAN_PAYMENT_TEST_IDS = {
  planSelect: SHELL.newMemberPlanSelect, startDateInput: SHELL.newMemberStartDateInput,
  discountInput: SHELL.newMemberDiscountInput, planNextButton: SHELL.newMemberPlanNextButton,
  paymentMode: SHELL.newMemberPaymentMode, txnRefInput: SHELL.newMemberTxnRefInput,
  paymentAmount: SHELL.newMemberPaymentAmount, netPayable: SHELL.newMemberNetPayable,
  outstandingBalance: SHELL.newMemberOutstandingBalance, paymentSubmit: SHELL.newMemberPaymentSubmit,
  invoiceButton: SHELL.newMemberInvoiceButton, continueButton: SHELL.newMemberPaymentContinueButton,
};

// The "+ New member" button lives in every shell's top bar (Owner Cockpit here) — per explicit
// product decision, it must follow the exact same 3-step enrollment (Personal -> Membership &
// Payment -> Health & Goals) as the Front Desk's Walk-in flow, not a shortcut. Reuses the same
// shared step components; only new thing here is a Branch picker (Owner Cockpit works across
// branches, unlike Desk which is already scoped to one via DeskDataContext) needed for Step 2's
// payment (`branch_id` is required on `POST /payments`).
export function NewMemberDialog({ open, onOpenChange }) {
  const { branches, selectedBranchId } = useAppData();
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();
  const [phase, setPhase] = useState("personal"); // personal -> existing -> plan_payment -> health
  const [form, setForm] = useState({ name: "", phone: "" });
  const [branchId, setBranchId] = useState(selectedBranchId !== "all" ? selectedBranchId : "");
  const [personalForm, setPersonalForm] = useState(EMPTY_PERSONAL_FORM);
  const [member, setMember] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!branches.length) return;
    if (selectedBranchId !== "all") setBranchId(selectedBranchId);
    else if (branches.length === 1) setBranchId(branches[0].id);
  }, [open, branches, selectedBranchId]);

  const reset = () => {
    setPhase("personal"); setForm({ name: "", phone: "" }); setBranchId(selectedBranchId !== "all" ? selectedBranchId : "");
    setPersonalForm(EMPTY_PERSONAL_FORM); setMember(null); setError("");
  };
  const close = () => { onOpenChange(false); reset(); };
  const finish = (message) => {
    celebrate("member_added", message);
    queryClient.invalidateQueries({ queryKey: ["members"] });
    close();
  };

  const detailsValid = isFieldValid("text", true, form.name) && isFieldValid("tel", true, form.phone)
    && !!branchId && isPersonalFormValid(personalForm);

  const createMemberMutation = useMutation({
    mutationFn: () => api.post("/members", { name: form.name, phone: form.phone, branch_id: branchId, ...buildPersonalPayload(personalForm) }),
    onSuccess: (res) => { setMember(res.data); setPhase("plan_payment"); },
    onError: (e) => {
      if (e.response?.status === 409) setPhase("existing");
      else setError(formatApiError(e).message);
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(v); }}>
      <DialogContent className="glass-modal sm:rounded-[14px] max-h-[85vh] overflow-y-auto" data-testid={SHELL.newMemberDialog}>
        <DialogHeader>
          <DialogTitle>New member</DialogTitle>
          <DialogDescription>
            {phase === "personal" && "Step 1 of 3 — Personal details. They'll appear on the cockpit immediately."}
            {phase === "plan_payment" && "Step 2 of 3 — Membership & payment"}
            {phase === "health" && "Step 3 of 3 — Health & goals"}
            {phase === "existing" && "Already on file"}
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }} data-testid={SHELL.newMemberError}>{error}</p>}

        {phase === "personal" && (
          <>
            <div className="space-y-4">
              <FormField label="Full name" required testId={SHELL.newMemberNameInput} value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <FormField label="Mobile" required type="tel" testId={SHELL.newMemberPhoneInput} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="9800000000" />
              {selectedBranchId === "all" && branches.length > 1 && (
                <div className="space-y-1.5">
                  <Label>Branch <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
                  <Select onValueChange={setBranchId} value={branchId || undefined}>
                    <SelectTrigger data-testid={SHELL.newMemberBranchSelect}><SelectValue placeholder="Which branch is this member joining?" /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <MemberPersonalFields form={personalForm} onChange={(k, v) => setPersonalForm((f) => ({ ...f, [k]: v }))} />
            </div>
            <DialogFooter>
              <SubmitButton
                testId={SHELL.newMemberNextButton} disabled={!detailsValid}
                pending={createMemberMutation.isPending} onClick={() => { setError(""); createMemberMutation.mutate(); }}
                label="Continue"
              />
            </DialogFooter>
          </>
        )}

        {phase === "existing" && (
          <>
            <p className="text-sm" data-testid={SHELL.newMemberExistingFound} style={{ color: "var(--scarlet-text)" }}>
              A member with this phone number already exists — reactivate their account from Members instead of creating a duplicate.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPhase("personal")}>Back</Button>
              <Button onClick={close} className="text-white" style={{ background: "var(--scarlet-accent)" }}>Done</Button>
            </DialogFooter>
          </>
        )}

        {phase === "plan_payment" && (
          <MembershipPaymentStep
            member={member} branchId={branchId} allowSkip={false} testIds={PLAN_PAYMENT_TEST_IDS}
            onComplete={() => setPhase("health")}
          />
        )}

        {phase === "health" && (
          <HealthGoalsFields member={member} pageTestId={DESK.walkinHealthPage} onDone={() => finish(`${member?.profile?.name} added as a new member.`)} />
        )}
      </DialogContent>
    </Dialog>
  );
}
